import {
  BlockquotePlugin,
  BoldPlugin,
  H1Plugin,
  H2Plugin,
  H3Plugin,
  ItalicPlugin,
  UnderlinePlugin,
} from '@platejs/basic-nodes/react';
import type { NodeComponent } from '@platejs/core';
import { IndentPlugin } from '@platejs/indent/react';
import { LinkPlugin } from '@platejs/link/react';
import {
  BulletedListPlugin,
  ListItemPlugin,
  ListPlugin,
  NumberedListPlugin,
} from '@platejs/list-classic/react';
import { ImagePlugin } from '@platejs/media/react';
import { KEYS } from 'platejs';
import { createPlatePlugin } from 'platejs/react';
import { createElement } from 'react';

import { EMOJI_EDITOR_PLUGINS } from '@/lib/crm/components/emoji-editor-plugins';

import { LinkElement } from './LinkElement';
import { LinkFloatingToolbar } from './LinkFloatingToolbar';
import { SlotElement } from './SlotElement';

const AfterEditableToolbar = () => createElement(LinkFloatingToolbar);

// Inline void: the chip sits inside a paragraph's prose (like a link or a
// mention), not as its own block.
const SlotPlugin = createPlatePlugin({
  key: 'slot',
  node: { isElement: true, isVoid: true, isInline: true },
}).withComponent(SlotElement);

// Built per-call from an injected renderer, exactly like ImagePlugin below —
// the caller memoizes the plugin array so usePlateEditor still receives a
// stable plugin identity (see PlateEmailEditor's useMemo over the renderers).
export function createEmailEditorPlugins(
  ImageElementRenderer: NodeComponent,
  SignatureElementRenderer: NodeComponent
) {
  return [
    BoldPlugin,
    ItalicPlugin,
    UnderlinePlugin,
    LinkPlugin.configure({
      render: {
        node: LinkElement,
        afterEditable: AfterEditableToolbar,
      },
      options: {
        // Auto-format URLs: add https:// if no protocol is specified
        transformInput: (url: string) => {
          if (!url) {
            return url;
          }
          // If URL already has a protocol or is a relative/anchor link, return as-is
          if (
            /^(https?:\/\/|mailto:|tel:)/i.test(url) ||
            url.startsWith('/') ||
            url.startsWith('#')
          ) {
            return url;
          }
          // Otherwise, prepend https://
          return `https://${url}`;
        },
      },
    }),
    H1Plugin,
    H2Plugin,
    H3Plugin,
    BlockquotePlugin,
    ListPlugin,
    BulletedListPlugin,
    NumberedListPlugin,
    ListItemPlugin,
    IndentPlugin.configure({
      inject: {
        targetPlugins: [KEYS.p, ...KEYS.heading, KEYS.blockquote],
      },
    }),
    ImagePlugin.configure({
      render: {
        node: ImageElementRenderer,
      },
    }),
    createPlatePlugin({
      key: 'signature',
      node: { isElement: true, isVoid: true },
    }).withComponent(SignatureElementRenderer),
    SlotPlugin,
    ...EMOJI_EDITOR_PLUGINS,
  ];
}
